import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowClockwise, BookOpen, ChatCircle, CheckCircle, FileMagnifyingGlass, FileText, ListChecks, ListMagnifyingGlass, MagnifyingGlass, Robot, Table, Translate, Trash, UploadSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { AskTab, ChangelogTab, CoverageTab, GapReportTab, MandatoryLanguageTab, PolicyCheckTab, RequirementsTab, ReviewQueueTab, StepNote, SummaryTab } from "../components/KnowledgeAnalysisTabs";

const SOURCE_TYPES = [
  ["regulation", "Law / regulation"],
  ["survey_guidance", "State survey / licensing guidance"],
  ["payer_guidance", "Payer / MCO guidance"],
  ["agency_policy", "Agency-approved policy"],
  ["handbook", "Handbook"],
  ["orientation", "Orientation document"],
  ["training_material", "Training material"],
  ["letter_workflow", "Letter / workflow reference"],
  ["other", "Other"],
];

const AUTHORITY_LEVELS = [
  ["federal_law_regulation", "0 · Federal law/CMS guidance"],
  ["pa_regulation", "1 · PA law/regulation"],
  ["state_guidance", "2 · State survey/licensing guidance"],
  ["payer_requirement", "3 · Payer/MCO requirement"],
  ["agency_policy", "4 · Agency-approved policy"],
  ["training_orientation", "5 · Training/orientation material"],
  ["letter_workflow", "6 · Letters/workflows"],
];

const STATUS_META = {
  needs_review: ["Needs review", "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#7c4a03]"],
  in_review: ["In review", "border-primary/30 bg-accent text-accent-foreground"],
  approved: ["Approved", "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]"],
  archived: ["Archived", "border-stone-300 bg-stone-100 text-stone-500"],
};

const labelFor = (list, value) => (list.find(([key]) => key === value) || [null, value || "—"])[1];
const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");
const fmtSize = (n) => {
  if (!n) return "—";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const StatusBadge = ({ status, testid }) => {
  const [label, cls] = STATUS_META[status] || STATUS_META.needs_review;
  return <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`} data-testid={testid}>{label}</span>;
};

export default function KnowledgeBasePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("sources");
  const [sources, setSources] = useState([]);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [delBusy, setDelBusy] = useState(false);
  const [cmpBusy, setCmpBusy] = useState(false);
  const [cmpResult, setCmpResult] = useState(null);
  const [cmpLatest, setCmpLatest] = useState(null);

  const toggleChecked = (sid) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };

  const runCompare = async () => {
    setCmpBusy(true);
    setCmpResult(null);
    try {
      const { data } = await api.post("/knowledge/compare", {}, { timeout: 300000 });
      setCmpResult(data);
      toast.success(`Compared ${data.sources_compared} sources — ${data.findings.length} finding(s)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCmpBusy(false);
    }
  };

  const deleteSources = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} knowledge source${ids.length > 1 ? "s" : ""}? Their extracted chunks and requirements are removed too. This cannot be undone.`)) return;
    setDelBusy(true);
    try {
      if (ids.length === 1) {
        await api.delete(`/knowledge/sources/${ids[0]}`);
      } else {
        await api.post("/knowledge/sources/bulk-delete", { ids });
      }
      toast.success(`Deleted ${ids.length} source${ids.length > 1 ? "s" : ""}`);
      setCheckedIds(new Set());
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDelBusy(false);
    }
  };
  const [summary, setSummary] = useState({});
  const [inventory, setInventory] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [topics, setTopics] = useState([]);
  const [orientationSteps, setOrientationSteps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedPaths, setSelectedPaths] = useState({});
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [policyId, setPolicyId] = useState("");
  const [upload, setUpload] = useState({ title: "", source_type: "regulation", jurisdiction: "Pennsylvania", authority_level: "pa_regulation", effective_date: "", owner: user?.name || "", tags: "", files: [] });
  const [fetchUrl, setFetchUrl] = useState("");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setSelected(null); setMapReview(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);



  const downloadTransfer = async () => {
    setBusy("transfer-dl");
    try {
      const r = await api.get("/knowledge/transfer/export", { responseType: "blob", timeout: 300000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "silver_kb_transfer.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Transfer bundle downloaded — upload it on the live site after deploy");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };
  const importTransfer = async (f) => {
    if (!f) return;
    setBusy("transfer-im");
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/knowledge/transfer/import", fd, { timeout: 300000 });
      toast.success(`Imported ${data.imported} source(s) — ${data.skipped_existing} already here`);
      (data.failed || []).forEach((m) => toast.error(m));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };
  const fetchFromUrl = async () => {
    if (!fetchUrl.trim().startsWith("http")) {
      toast.error("Enter a full URL starting with http(s)://");
      return;
    }
    setBusy("fetch");
    try {
      const { data } = await api.post("/knowledge/sources/fetch-url", { url: fetchUrl.trim() }, { timeout: 90000 });
      toast.success(`Registered: ${data.source.title} (v${data.source.version}) — review it in the Review Queue`);
      setFetchUrl("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const [s, inv, p, t, o] = await Promise.all([
        api.get("/knowledge/sources"),
        api.get("/knowledge/inventory"),
        api.get("/policies"),
        api.get("/training/topics"),
        api.get("/orientation"),
      ]);
      setSources(s.data.sources || []);
      setSummary(s.data.summary || {});
      setInventory(inv.data.items || []);
      setPolicies(p.data || []);
      setTopics((t.data || []).filter((x) => x.active !== false));
      setOrientationSteps((o.data.steps || []).filter((x) => x.active !== false));
      api.get("/knowledge/compare/latest").then((r) => setCmpLatest(r.data.run)).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role]);

  const filteredSources = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sources.filter((s) => (statusFilter === "all" ? s.review_status !== "archived" : s.review_status === statusFilter)
      && (!q || [s.title, s.filename, s.source_type, s.jurisdiction, ...(s.tags || [])].join(" ").toLowerCase().includes(q)));
  }, [sources, query, statusFilter]);

  const openSource = async (sid) => {
    setBusy(`open-${sid}`);
    try {
      const { data } = await api.get(`/knowledge/sources/${sid}`);
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const uploadSource = async () => {
    const files = upload.files || [];
    if (!files.length || (files.length === 1 && !upload.title.trim())) {
      toast.error(files.length > 1 ? "Choose at least one file" : "Choose a file and enter a title");
      return;
    }
    let done = 0, failed = 0;
    for (const file of files) {
      setBusy(`upload-${done + failed + 1}/${files.length}`);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const meta = { ...upload };
        if (files.length > 1 || !upload.title.trim()) meta.title = (file.name || "Untitled").replace(/\.[^.]+$/, "").replaceAll("_", " ");
        for (const [key, value] of Object.entries(meta)) {
          if (key !== "files" && value != null) fd.append(key, value);
        }
        await api.post("/knowledge/sources", fd, { timeout: 180000 });
        done++;
      } catch (e) {
        failed++;
        toast.error(`${file.name}: ${formatApiError(e)}`);
      }
    }
    toast.success(`Registered ${done} source(s)${failed ? ` — ${failed} failed` : ""}`);
    setUpload({ ...upload, title: "", tags: "", effective_date: "", files: [] });
    setBusy("");
    load();
  };

  const importSelectedInventory = async () => {
    const paths = Object.keys(selectedPaths).filter((p) => selectedPaths[p]);
    if (!paths.length) {
      toast.error("Select at least one inventory file");
      return;
    }
    setBusy("inventory");
    try {
      const { data } = await api.post("/knowledge/inventory/import", { paths }, { timeout: 180000 });
      toast.success(`Registered ${data.imported} source version(s); ${data.skipped} skipped`);
      setSelectedPaths({});
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const registerPolicy = async () => {
    if (!policyId) {
      toast.error("Choose a policy first");
      return;
    }
    setBusy("policy");
    try {
      const { data } = await api.post("/knowledge/sources/from-policy", { policy_id: policyId }, { timeout: 60000 });
      toast.success(`Policy registered as source version ${data.source.version}`);
      setPolicyId("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const saveSelected = async (updates) => {
    if (!selected) return;
    setBusy("save-source");
    try {
      await api.put(`/knowledge/sources/${selected.source.id}`, updates);
      toast.success("Source updated");
      await openSource(selected.source.id);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const reviewSource = async (action) => {
    setBusy(`review-${action}`);
    try {
      await api.post(`/knowledge/sources/${selected.source.id}/review`, { action });
      toast.success(action === "approved" ? "Source approved into the knowledge base" : "Source status updated");
      await openSource(selected.source.id);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const reextractSource = async () => {
    setBusy("ocr");
    try {
      const { data } = await api.post(`/knowledge/sources/${selected.source.id}/reextract`, {}, { timeout: 300000 });
      toast.success(`OCR/extraction complete: ${data.chunk_count} chunks`);
      await openSource(selected.source.id);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const aiClassify = async () => {
    setBusy("ai");
    try {
      const { data } = await api.post(`/knowledge/sources/${selected.source.id}/ai-classify`, {}, { timeout: 120000 });
      toast.success("Classification done — extracting requirements next…");
      setSelected((s) => ({ ...s, source: data.source }));
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setBusy("");
    }
  };

  const applyAi = async () => {
    setBusy("apply-ai");
    try {
      const { data } = await api.post(`/knowledge/sources/${selected.source.id}/apply-classification`);
      toast.success("AI suggestions applied to metadata; mappings remain reviewable");
      setSelected((s) => ({ ...s, source: data.source }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const saveMappings = async (mappings, notes) => {
    setBusy("mappings");
    try {
      await api.put(`/knowledge/sources/${selected.source.id}/mappings`, { ...mappings, notes });
      toast.success("Approved mappings saved");
      await openSource(selected.source.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const runSearch = async () => {
    if (!query.trim()) return;
    setBusy("search");
    try {
      const { data } = await api.get(`/knowledge/search?q=${encodeURIComponent(query.trim())}`);
      setSearchResults(data.results || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const [analyzing, setAnalyzing] = useState(null);
  const analyzeCancel = useRef(false);

  const analyzeAll = async () => {
    const targets = sources.filter((s) => s.review_status !== "archived" && s.extraction_status === "extracted");
    if (!targets.length) {
      toast.info("No analyzable sources — sources need extracted text first");
      return;
    }
    analyzeCancel.current = false;
    setAnalyzing({ total: targets.length, idx: 0, current: "", phase: "", processed: 0, chunks: 0, classified: 0, skippedClassify: 0, failed: [], doneAll: false });
    for (let i = 0; i < targets.length; i++) {
      if (analyzeCancel.current) break;
      const s = targets[i];
      setAnalyzing((a) => ({ ...a, idx: i, current: s.title, phase: s.ai_classification ? "extracting" : "classifying", processed: 0, chunks: 0 }));
      try {
        if (!s.ai_classification) {
          await api.post(`/knowledge/sources/${s.id}/ai-classify`, {}, { timeout: 120000 });
          setAnalyzing((a) => ({ ...a, classified: a.classified + 1, phase: "extracting" }));
        } else {
          setAnalyzing((a) => ({ ...a, skippedClassify: a.skippedClassify + 1 }));
        }
        for (let j = 0; j < 120; j++) {
          if (analyzeCancel.current) break;
          const { data } = await api.post(`/knowledge/sources/${s.id}/extract-requirements`, {}, { timeout: 95000 });
          setAnalyzing((a) => ({ ...a, processed: data.processed_chunks, chunks: data.total_chunks }));
          if (data.done) break;
        }
      } catch (e) {
        setAnalyzing((a) => ({ ...a, failed: [...(a.failed || []), s.title] }));
      }
    }
    const cancelled = analyzeCancel.current;
    setAnalyzing((a) => ({ ...a, doneAll: true, phase: cancelled ? "cancelled" : "done" }));
    toast.success(cancelled ? "Batch analysis stopped — it resumes where it left off next time" : "Batch analysis finished — results are in the Review Queue and Requirements tab");
    load();
  };

  const [mapReview, setMapReview] = useState(null);
  const [mapDrafts, setMapDrafts] = useState({});

  const openMapReview = async () => {
    setBusy("map-review");
    try {
      const { data } = await api.get("/knowledge/mappings/pending");
      const items = data.items || [];
      setMapReview(items);
      const drafts = {};
      for (const it of items) {
        drafts[it.id] = {
          policy_ids: [...new Set([...it.approved.policy_ids, ...it.suggested.policy_ids])],
          training_topic_ids: [...new Set([...it.approved.training_topic_ids, ...it.suggested.training_topic_ids])],
          orientation_step_ids: [...new Set([...it.approved.orientation_step_ids, ...it.suggested.orientation_step_ids])],
        };
      }
      setMapDrafts(drafts);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const toggleMapDraft = (sid, key, id) =>
    setMapDrafts((d) => {
      const cur = d[sid] || { policy_ids: [], training_topic_ids: [], orientation_step_ids: [] };
      const list = cur[key] || [];
      return { ...d, [sid]: { ...cur, [key]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] } };
    });

  const saveAllMappings = async () => {
    const removed = (mapReview || []).reduce((n, it) => {
      const d = mapDrafts[it.id] || {};
      return n + ["policy_ids", "training_topic_ids", "orientation_step_ids"]
        .reduce((m, key) => m + (it.approved[key] || []).filter((x) => !(d[key] || []).includes(x)).length, 0);
    }, 0);
    if (removed > 0 && !window.confirm(`${removed} previously saved mapping(s) are now unchecked and will be removed when you save. Continue?`)) return;
    setBusy("map-save-all");
    try {
      const items = (mapReview || []).map((it) => ({ source_id: it.id, ...(mapDrafts[it.id] || {}) }));
      const { data } = await api.post("/knowledge/mappings/bulk-approve", { items });
      toast.success(`Saved mappings for ${data.saved} source(s)${data.errors?.length ? ` — failed: ${data.errors.join(", ")}` : ""}`);
      setMapReview(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  if (user?.role !== "admin") {
    return <div className="rounded-lg border border-stone-200 bg-white p-8 text-sm text-stone-500">Knowledge Base is admin-only.</div>;
  }

  return (
    <div className="max-w-7xl" data-testid="knowledge-page">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Knowledge Base</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-stone-500">
            Immutable source documents, extracted knowledge chunks, authority levels, review status, and approved links to policies and training. Policies remain separate operational documents.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={analyzeAll} disabled={!!analyzing && !analyzing.doneAll} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="analyze-all-btn">
            <Robot size={15} className={`mr-1.5 ${analyzing && !analyzing.doneAll ? "animate-pulse" : ""}`} />
            {analyzing && !analyzing.doneAll ? `Analyzing ${analyzing.idx + 1}/${analyzing.total}…` : "Analyze all sources (AI)"}
          </Button>
          <Button onClick={openMapReview} disabled={busy === "map-review"} variant="outline" className="border-primary/40 text-accent-foreground" data-testid="review-mappings-btn">
            <ListChecks size={15} className="mr-1.5" /> Review suggested mappings
          </Button>
          <Button variant="outline" onClick={load} className="border-stone-300" data-testid="knowledge-refresh"><ArrowClockwise size={15} className="mr-1.5" /> Refresh</Button>
          <Button variant="outline" onClick={runCompare} disabled={cmpBusy} className="border-stone-300" data-testid="knowledge-compare">
            <FileMagnifyingGlass size={15} className="mr-1.5" /> {cmpBusy ? "Comparing…" : "Check superseded / conflicts"}
          </Button>
          <a href={`${api.defaults.baseURL}/knowledge/sources/export`} download
            className="inline-flex h-9 items-center rounded-md border border-stone-300 bg-white px-3 text-sm font-medium text-stone-700 hover:bg-stone-900 hover:text-white"
            data-testid="knowledge-export-list">
            Download list (CSV)
          </a>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="knowledge-summary">
        {[["sources", "Source versions"], ["latest_sources", "Latest sources"], ["approved", "Approved"], ["needs_review", "Needs review"], ["chunks", "Knowledge chunks"]].map(([key, label]) => (
          <div key={key} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={`knowledge-summary-${key}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">{label}</p>
            <p className="mt-2 font-heading text-3xl font-semibold text-stone-900">{summary[key] || 0}</p>
          </div>
        ))}
      </div>

      {analyzing && (
        <div className="mb-6 rounded-lg border border-primary/30 bg-accent/40 p-4" data-testid="analyze-progress-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-stone-800" data-testid="analyze-progress-title">
              {analyzing.doneAll
                ? analyzing.phase === "cancelled" ? "Batch analysis stopped — nothing was auto-approved" : "Batch analysis complete"
                : `Analyzing source ${analyzing.idx + 1} of ${analyzing.total}: ${analyzing.current}`}
            </p>
            {!analyzing.doneAll && (
              <Button size="sm" variant="outline" onClick={() => { analyzeCancel.current = true; }} className="border-stone-300" data-testid="analyze-cancel-btn">Stop after this source</Button>
            )}
            {analyzing.doneAll && (
              <Button size="sm" variant="outline" onClick={() => setAnalyzing(null)} className="border-stone-300" data-testid="analyze-dismiss-btn">Dismiss</Button>
            )}
          </div>
          {!analyzing.doneAll && (
            <>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.round((analyzing.idx / analyzing.total) * 100)}%` }} />
              </div>
              <p className="mt-2 text-xs text-stone-500" data-testid="analyze-progress-detail">
                {analyzing.phase === "classifying" ? "Classifying document and suggesting mappings…" : `Extracting requirements — section ${analyzing.processed} of ${analyzing.chunks || "…"}`}
                {" "}Keep this tab open; it works through the list one source at a time. Everything lands as pending review — nothing is auto-approved.
              </p>
            </>
          )}
          {analyzing.doneAll && (
            <p className="mt-1 text-xs text-stone-600" data-testid="analyze-progress-summary">
              Classified {analyzing.classified} source(s){analyzing.skippedClassify ? `, ${analyzing.skippedClassify} already classified` : ""}. Requirements are waiting in the Requirements tab as pending review.
              {analyzing.failed.length > 0 && <span className="mt-1 block text-[#C0392B]">Failed (retry them individually): {analyzing.failed.join("; ")}</span>}
            </p>
          )}
        </div>
      )}

      {!cmpResult && cmpLatest && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-xs text-stone-500" data-testid="knowledge-last-compare">
          <span>Last automatic KB check: {String(cmpLatest.ran_at).slice(0, 10)} — {(cmpLatest.findings || []).length} finding(s) across {cmpLatest.sources_compared} sources</span>
          {(cmpLatest.findings || []).length > 0 && (
            <button onClick={() => setCmpResult({ findings: cmpLatest.findings, sources_compared: cmpLatest.sources_compared })}
              className="font-semibold text-primary hover:underline" data-testid="knowledge-last-compare-view">View findings</button>
          )}
        </div>
      )}

      {cmpResult && (
        <div className="mb-6 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="knowledge-compare-results">
          <div className="flex items-center justify-between">
            <p className="font-heading text-sm font-semibold text-stone-900">
              Comparison findings — {cmpResult.findings.length} issue(s) across {cmpResult.sources_compared} sources
            </p>
            <button onClick={() => setCmpResult(null)} className="text-stone-400 hover:text-stone-700" data-testid="knowledge-compare-close"><X size={16} /></button>
          </div>
          {cmpResult.findings.length === 0 ? (
            <p className="mt-2 text-sm text-stone-500">No superseded documents or conflicts found.</p>
          ) : (
            <div className="mt-3 space-y-3">
              {cmpResult.findings.map((f, i) => (
                <div key={i} className="rounded-md border border-stone-200 bg-stone-50 p-3" data-testid={`knowledge-finding-${i}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${f.kind === "superseded" ? "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#7c4a03]" : "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"}`}>
                      {f.kind === "superseded" ? "SUPERSEDED" : "CONFLICT"}
                    </span>
                    <span className="text-sm font-semibold text-stone-800">{f.title || "Finding"}</span>
                  </div>
                  <p className="mt-1 text-xs text-stone-500">Documents: {f.sources.join(" · ")}</p>
                  {f.detail && <p className="mt-1.5 text-sm text-stone-700">{f.detail}</p>}
                  {f.recommendation && <p className="mt-1.5 text-xs font-medium text-stone-600">Recommendation: {f.recommendation}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-1 rounded-lg border border-stone-200 bg-white p-1 shadow-sm" data-testid="knowledge-tabs">
        {[["sources", "Source Library", BookOpen], ["queue", "Review Queue", ListChecks], ["language", "Mandatory Language", Translate], ["gap", "Gap Report", FileMagnifyingGlass], ["coverage", "Coverage Matrix", Table], ["policycheck", "Policy Check", FileMagnifyingGlass], ["summary", "Summary", FileText], ["changelog", "Change Log", FileText], ["requirements", "Requirements", ListMagnifyingGlass], ["ask", "Ask", ChatCircle], ["inventory", "Upload & Inventory", UploadSimple], ["search", "Search Knowledge", MagnifyingGlass]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} className={`flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium ${tab === key ? "bg-primary text-primary-foreground" : "text-stone-600 hover:bg-accent"}`} data-testid={`knowledge-tab-${key}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {tab === "sources" && (
        <section className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="knowledge-sources-panel">
          <div className="border-b border-stone-200 px-4 pt-4 pb-3">
            <StepNote step="Step 1 of 5" testid="stepnote-sources">
              Locked snapshots of your authority documents. Uploads are read-only originals (new uploads become new versions). Nothing here affects staff, policies, or reports until you approve it in the Review Queue. Click a source to read it, run AI classification, or extract requirements.
            </StepNote>
          </div>
          <div className="flex flex-wrap gap-3 border-b border-stone-200 p-4">
            <div className="min-w-64 flex-1">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search title, filename, tag, jurisdiction..." data-testid="knowledge-filter-search" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="knowledge-status-filter">
              <option value="all">All active statuses</option>
              <option value="needs_review">Needs review</option>
              <option value="in_review">In review</option>
              <option value="approved">Approved</option>
              <option value="archived">Archived</option>
            </select>
            {checkedIds.size > 0 && (
              <Button onClick={() => deleteSources([...checkedIds])} disabled={delBusy} className="h-9 bg-[#C0392B] text-white hover:bg-[#96281B]" data-testid="knowledge-delete-selected">
                <Trash size={15} className="mr-1.5" /> {delBusy ? "Deleting…" : `Delete selected (${checkedIds.size})`}
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-left text-sm">
              <thead className="border-b border-stone-200 bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
                <tr>
                  <th className="px-3 py-3 w-8"><input type="checkbox" aria-label="Select all sources" data-testid="knowledge-select-all"
                    checked={filteredSources.length > 0 && filteredSources.every((s) => checkedIds.has(s.id))}
                    onChange={(e) => setCheckedIds(e.target.checked ? new Set(filteredSources.map((s) => s.id)) : new Set())} /></th>
                  <th className="px-4 py-3">Source</th><th className="px-4 py-3">Type / authority</th><th className="px-4 py-3">Version</th><th className="px-4 py-3">Extraction</th><th className="px-4 py-3">Mappings</th><th className="px-4 py-3">Status</th><th className="px-3 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filteredSources.map((s) => (
                  <tr key={s.id} onClick={() => openSource(s.id)} className="cursor-pointer border-b border-stone-100 last:border-0 hover:bg-accent/40" data-testid={`knowledge-source-${s.id}`}>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={`Select ${s.title}`} checked={checkedIds.has(s.id)} onChange={() => toggleChecked(s.id)} data-testid={`knowledge-check-${s.id}`} />
                    </td>
                    <td className="px-4 py-3"><p className="font-medium text-stone-900">{s.doc_no && <span className="mr-1.5 rounded bg-accent px-1.5 py-0.5 text-[10px] font-bold text-accent-foreground" data-testid={`doc-no-${s.id}`}>{s.doc_no}</span>}{s.title}</p><p className="text-xs text-stone-400">{s.filename} · {fmtSize(s.size)}</p></td>
                    <td className="px-4 py-3 text-xs text-stone-600">{labelFor(SOURCE_TYPES, s.source_type)}<br />{labelFor(AUTHORITY_LEVELS, s.authority_level)}</td>
                    <td className="px-4 py-3">v{s.version}</td>
                    <td className="px-4 py-3 text-xs text-stone-600">{s.extraction_status === "extracted" ? `${s.chunk_count} chunks` : "OCR needed"}<br />{s.page_count || 0} page/section(s)</td>
                    <td className="px-4 py-3 text-xs text-stone-600">{(s.approved_policy_ids || []).length} policies · {(s.approved_training_topic_ids || []).length} trainings</td>
                    <td className="px-4 py-3"><StatusBadge status={s.review_status} testid={`source-status-${s.id}`} /></td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => deleteSources([s.id])} disabled={delBusy} title="Delete source permanently" className="text-stone-400 hover:text-[#C0392B]" data-testid={`knowledge-delete-${s.id}`}>
                        <Trash size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredSources.length === 0 && <tr><td colSpan="8" className="px-4 py-12 text-center text-sm text-stone-400">No sources match.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "queue" && <ReviewQueueTab onOpen={openSource} onChanged={load} />}

      {tab === "language" && <MandatoryLanguageTab />}

      {tab === "gap" && <GapReportTab />}

      {tab === "coverage" && <CoverageTab />}

      {tab === "policycheck" && <PolicyCheckTab policies={policies} />}

      {tab === "summary" && <SummaryTab />}

      {tab === "changelog" && <ChangelogTab />}

      {tab === "requirements" && <RequirementsTab policies={policies} topics={topics} sources={sources} />}

      {tab === "ask" && <AskTab sources={sources} />}

      {tab === "inventory" && (
        <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
          <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="knowledge-upload-panel">
            <h2 className="font-heading text-xl font-semibold text-stone-900">Register new source</h2>
            <div className="mt-4 space-y-3">
              <div><Label>Title</Label><Input value={upload.title} onChange={(e) => setUpload({ ...upload, title: e.target.value })} data-testid="source-title-input" /></div>
              <div><Label>File</Label><input type="file" multiple accept=".pdf,.docx,.txt,.md,.csv,.xlsx" onChange={(e) => { const picked = [...(e.target.files || [])]; setUpload({ ...upload, files: picked, title: upload.title || (picked[0]?.name || "").replace(/\.[^.]+$/, "").replaceAll("_", " ") }); }} className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="source-file-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Source type</Label><select value={upload.source_type} onChange={(e) => setUpload({ ...upload, source_type: e.target.value })} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="source-type-select">{SOURCE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                <div><Label>Authority</Label><select value={upload.authority_level} onChange={(e) => setUpload({ ...upload, authority_level: e.target.value })} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="source-authority-select">{AUTHORITY_LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Jurisdiction</Label><Input value={upload.jurisdiction} onChange={(e) => setUpload({ ...upload, jurisdiction: e.target.value })} data-testid="source-jurisdiction-input" /></div>
                <div><Label>Effective date</Label><Input type="date" value={upload.effective_date} onChange={(e) => setUpload({ ...upload, effective_date: e.target.value })} data-testid="source-effective-date" /></div>
              </div>
              <div><Label>Owner</Label><Input value={upload.owner} onChange={(e) => setUpload({ ...upload, owner: e.target.value })} data-testid="source-owner-input" /></div>
              <div><Label>Tags (comma separated)</Label><Input value={upload.tags} onChange={(e) => setUpload({ ...upload, tags: e.target.value })} data-testid="source-tags-input" /></div>
              <Button onClick={uploadSource} disabled={!!busy.startsWith("upload")} className="w-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="source-upload-button">{busy.startsWith("upload-") ? `Registering ${busy.slice(7)}…` : busy === "upload" ? "Registering..." : "Register Source(s)"}</Button>
            </div>

            <div className="mt-8 border-t border-stone-200 pt-5">
              <h3 className="font-heading text-lg font-semibold text-stone-900">Fetch from URL</h3>
              <p className="mt-1 text-xs text-stone-500">Paste a bulletin, regulation, or guidance link (web page or direct PDF). The page text is snapshotted as an immutable source version — re-fetch the same URL later to version updates.</p>
              <Input value={fetchUrl} onChange={(e) => setFetchUrl(e.target.value)} placeholder="https://www.pa.gov/..." className="mt-3" data-testid="source-url-input" />
              <Button onClick={fetchFromUrl} disabled={busy === "fetch"} variant="outline" className="mt-3 w-full border-stone-300" data-testid="source-fetch-button">{busy === "fetch" ? "Fetching..." : "Fetch & Register"}</Button>
            </div>

            <div className="mt-8 border-t border-stone-200 pt-5">
              <h3 className="font-heading text-lg font-semibold text-stone-900">Register reviewed policy as knowledge</h3>
              <p className="mt-1 text-xs text-stone-500">Creates an immutable source version from the current policy content without changing the operational policy.</p>
              <select value={policyId} onChange={(e) => setPolicyId(e.target.value)} className="mt-3 h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="policy-source-select">
                <option value="">Choose a policy…</option>
                {policies.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.status} · v{p.version || 1}</option>)}
              </select>
              <Button onClick={registerPolicy} disabled={busy === "policy"} variant="outline" className="mt-3 w-full border-stone-300" data-testid="policy-source-register">Register Policy as Source</Button>
            </div>

            <div className="mt-8 border-t border-stone-200 pt-5" data-testid="transfer-card">
              <h3 className="font-heading text-lg font-semibold text-stone-900">Go-live transfer</h3>
              <p className="mt-1 text-xs text-stone-500">Carries the whole knowledge base — every source and its original file — between preview and the live site. Download the bundle here, then upload it on the live site after deploy. Safe to re-run: sources already there are skipped.</p>
              <Button onClick={downloadTransfer} disabled={busy === "transfer-dl"} variant="outline" className="mt-3 w-full border-stone-300" data-testid="transfer-download">{busy === "transfer-dl" ? "Building bundle…" : "Download transfer bundle (ZIP)"}</Button>
              <label className="mt-2 flex w-full cursor-pointer items-center justify-center rounded-md border border-dashed border-stone-300 px-3 py-2 text-sm font-medium text-stone-600 hover:bg-accent" data-testid="transfer-import-label">
                {busy === "transfer-im" ? "Importing…" : "Import a transfer bundle"}
                <input type="file" accept=".zip" className="hidden" data-testid="transfer-import-input" onChange={(e) => importTransfer(e.target.files?.[0])} />
              </label>
            </div>
          </section>

          <section className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="knowledge-inventory-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 p-4">
              <div><h2 className="font-heading text-xl font-semibold text-stone-900">Existing foundational-document inventory</h2><p className="text-xs text-stone-500">Select files to register. Employee evidence archives are intentionally excluded.</p></div>
              <Button onClick={importSelectedInventory} disabled={busy === "inventory"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="inventory-import-selected">{busy === "inventory" ? "Importing..." : "Register selected"}</Button>
            </div>
            <div className="max-h-[720px] overflow-y-auto p-4">
              <div className="space-y-2">
                {inventory.map((item) => (
                  <label key={item.path} className={`flex items-start gap-3 rounded-lg border p-3 ${item.registered ? "border-stone-200 bg-stone-50 opacity-70" : "border-stone-200 bg-white hover:bg-accent/30"}`} data-testid={`inventory-item-${item.path.replaceAll("/", "-")}`}>
                    <input type="checkbox" checked={!!selectedPaths[item.path]} disabled={item.registered} onChange={(e) => setSelectedPaths((x) => ({ ...x, [item.path]: e.target.checked }))} className="mt-1" data-testid={`inventory-select-${item.path.replaceAll("/", "-")}`} />
                    <span className="min-w-0 flex-1"><span className="block text-sm font-medium text-stone-800">{item.title}</span><span className="mt-0.5 block text-xs text-stone-400">{item.path} · {fmtSize(item.size)}</span></span>
                    {item.registered && <span className="rounded-full bg-stone-200 px-2 py-0.5 text-xs font-medium text-stone-600">Registered</span>}
                  </label>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}

      {tab === "search" && (
        <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="knowledge-search-panel">
          <div className="flex gap-2">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="Search approved knowledge chunks..." data-testid="knowledge-search-input" />
            <Button onClick={runSearch} disabled={busy === "search"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="knowledge-search-button">Search</Button>
          </div>
          <div className="mt-5 space-y-3" data-testid="knowledge-search-results">
            {searchResults === null && <p className="rounded-lg border border-dashed border-stone-300 p-8 text-center text-sm text-stone-400">Search the approved knowledge base by phrase, topic, policy, or training area.</p>}
            {searchResults?.length === 0 && <p className="rounded-lg border border-dashed border-stone-300 p-8 text-center text-sm text-stone-400">No approved knowledge chunks matched.</p>}
            {(searchResults || []).map((r) => (
              <button key={r.id} onClick={() => openSource(r.source_id)} className="w-full rounded-lg border border-stone-200 bg-white p-4 text-left hover:bg-accent/30" data-testid={`knowledge-result-${r.id}`}>
                <p className="text-sm font-semibold text-stone-900">{r.source_title} <span className="font-normal text-stone-400">v{r.source_version}</span></p>
                <p className="mt-1 text-xs text-stone-500">Citation: {r.section_title || "Section not identified"} · page {r.page_start || "—"}</p>
                <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-stone-600">{r.snippet}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      {mapReview && (
        <div className="fixed inset-0 z-50 flex justify-center bg-black/40 p-6" data-testid="mapping-review-modal">
          <div className="h-full w-full max-w-6xl overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="sticky top-0 z-10 flex items-start justify-between border-b border-stone-200 bg-white p-5">
              <div>
                <p className="font-heading text-2xl font-semibold text-stone-900">Review suggested mappings</p>
                <p className="mt-1 max-w-2xl text-xs text-stone-500">Every analyzed source with its AI-suggested links pre-checked and marked AI. Uncheck anything wrong, then save everything at once. Saving mappings does not approve the source itself — mappings start counting toward the Coverage Matrix and Gap Report once the source is approved.</p>
              </div>
              <button onClick={() => setMapReview(null)} className="text-stone-400 hover:text-stone-700" data-testid="mapping-review-close"><X size={22} /></button>
            </div>
            <div className="space-y-4 p-5">
              {mapReview.length === 0 && <p className="py-10 text-center text-sm text-stone-400" data-testid="mapping-review-empty">No AI mapping suggestions yet — run &ldquo;Analyze all sources&rdquo; first.</p>}
              {mapReview.map((it) => (
                <div key={it.id} className="rounded-lg border border-stone-200 p-4" data-testid={`map-review-${it.id}`}>
                  <p className="text-sm font-semibold text-stone-900">{it.title}
                    <span className={`ml-2 rounded-full border px-2 py-0.5 text-[10px] font-medium ${it.review_status === "approved" ? "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" : "border-stone-300 text-stone-500"}`}>{it.review_status === "approved" ? "Approved source" : "Not yet approved"}</span>
                  </p>
                  <div className="mt-3 grid gap-4 lg:grid-cols-3">
                    <MappingPicker title="Policies" items={policies} selected={mapDrafts[it.id]?.policy_ids || []} suggested={it.suggested.policy_ids} onToggle={(id) => toggleMapDraft(it.id, "policy_ids", id)} testid={`bulk-${it.id}-policy`} />
                    <MappingPicker title="Training topics" items={topics} selected={mapDrafts[it.id]?.training_topic_ids || []} suggested={it.suggested.training_topic_ids} onToggle={(id) => toggleMapDraft(it.id, "training_topic_ids", id)} testid={`bulk-${it.id}-training`} />
                    <MappingPicker title="Orientation steps" items={orientationSteps} selected={mapDrafts[it.id]?.orientation_step_ids || []} suggested={it.suggested.orientation_step_ids} onToggle={(id) => toggleMapDraft(it.id, "orientation_step_ids", id)} testid={`bulk-${it.id}-orientation`} />
                  </div>
                </div>
              ))}
              {mapReview.length > 0 && (
                <div className="sticky bottom-0 flex justify-end gap-2 border-t border-stone-200 bg-white py-4">
                  <Button variant="outline" onClick={() => setMapReview(null)} className="border-stone-300" data-testid="mapping-review-cancel">Cancel</Button>
                  <Button onClick={saveAllMappings} disabled={busy === "map-save-all"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="mapping-review-save-all">{busy === "map-save-all" ? "Saving…" : `Save mappings for all ${mapReview.length} source(s)`}</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selected && (
        <SourceDetail
          key={selected.source.id}
          selected={selected}
          setSelected={setSelected}
          busy={busy}
          saveSelected={saveSelected}
          reviewSource={reviewSource}
          aiClassify={aiClassify}
          applyAi={applyAi}
          saveMappings={saveMappings}
          reextractSource={reextractSource}
          policies={policies}
          topics={topics}
          orientationSteps={orientationSteps}
        />
      )}
    </div>
  );
}

function SourceDetail({ selected, setSelected, busy, saveSelected, reviewSource, aiClassify, applyAi, saveMappings, reextractSource, policies, topics, orientationSteps }) {
  const { source, chunks, versions } = selected;
  const [edit, setEdit] = useState({ title: source.title, source_type: source.source_type, jurisdiction: source.jurisdiction || "", authority_level: source.authority_level, effective_date: source.effective_date || "", owner: source.owner || "", tags: (source.tags || []).join(", ") });
  const ai = source.ai_classification;
  const aiSuggested = {
    policy_ids: source.suggested_policy_ids || ai?.suggested_policy_ids || [],
    training_topic_ids: source.suggested_training_topic_ids || ai?.suggested_training_topic_ids || [],
    orientation_step_ids: source.suggested_orientation_step_ids || ai?.suggested_orientation_step_ids || [],
  };
  const [mappings, setMappings] = useState({
    policy_ids: [...new Set([...(source.approved_policy_ids || []), ...aiSuggested.policy_ids])],
    training_topic_ids: [...new Set([...(source.approved_training_topic_ids || []), ...aiSuggested.training_topic_ids])],
    orientation_step_ids: [...new Set([...(source.approved_orientation_step_ids || []), ...aiSuggested.orientation_step_ids])],
  });
  const [mapNotes, setMapNotes] = useState(source.mapping_notes || {});
  useEffect(() => {
    setMappings((m) => ({
      policy_ids: [...new Set([...m.policy_ids, ...aiSuggested.policy_ids])],
      training_topic_ids: [...new Set([...m.training_topic_ids, ...aiSuggested.training_topic_ids])],
      orientation_step_ids: [...new Set([...m.orientation_step_ids, ...aiSuggested.orientation_step_ids])],
    }));
  }, [source.ai_classification]);

  const toggleMap = (key, id) => setMappings((m) => ({ ...m, [key]: m[key].includes(id) ? m[key].filter((x) => x !== id) : [...m[key], id] }));

  const [extract, setExtract] = useState(null);

  const runExtraction = async () => {
    setExtract({ processed: 0, total: null, created: 0 });
    try {
      let total = 0;
      for (let i = 0; i < 120; i++) {
        const { data } = await api.post(`/knowledge/sources/${source.id}/extract-requirements`, {}, { timeout: 95000 });
        total += data.created;
        setExtract({ processed: data.processed_chunks, total: data.total_chunks, created: total });
        if (data.done) break;
      }
      toast.success(`Extracted ${total} requirement(s) — review them in the Requirements tab`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExtract(null);
    }
  };

  const runFullAnalysis = async () => {
    const ok = await aiClassify();
    if (ok) await runExtraction();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" data-testid="knowledge-detail-modal">
      <div className="h-full w-full max-w-4xl overflow-y-auto bg-white shadow-xl">
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-stone-200 bg-white p-5">
          <div>
            <p className="font-heading text-2xl font-semibold text-stone-900">{source.title}</p>
            <p className="mt-1 text-xs text-stone-500">v{source.version} · {source.filename} · SHA-256 {String(source.file_hash || "").slice(0, 12)}…</p>
          </div>
          <button onClick={() => setSelected(null)} className="text-stone-400 hover:text-stone-700" data-testid="knowledge-detail-close"><X size={22} /></button>
        </div>

        <div className="space-y-6 p-5">
          <div className="grid gap-3 rounded-lg border border-stone-200 p-4 sm:grid-cols-3" data-testid="source-metadata-editor">
            <div className="sm:col-span-3"><Label>Title</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="detail-title" /></div>
            <div><Label>Source type</Label><select value={edit.source_type} onChange={(e) => setEdit({ ...edit, source_type: e.target.value })} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="detail-source-type">{SOURCE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><Label>Authority</Label><select value={edit.authority_level} onChange={(e) => setEdit({ ...edit, authority_level: e.target.value })} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="detail-authority">{AUTHORITY_LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><Label>Jurisdiction</Label><Input value={edit.jurisdiction} onChange={(e) => setEdit({ ...edit, jurisdiction: e.target.value })} data-testid="detail-jurisdiction" /></div>
            <div><Label>Effective date</Label><Input type="date" value={edit.effective_date} onChange={(e) => setEdit({ ...edit, effective_date: e.target.value })} data-testid="detail-effective-date" /></div>
            <div><Label>Owner</Label><Input value={edit.owner} onChange={(e) => setEdit({ ...edit, owner: e.target.value })} data-testid="detail-owner" /></div>
            <div><Label>Tags</Label><Input value={edit.tags} onChange={(e) => setEdit({ ...edit, tags: e.target.value })} data-testid="detail-tags" /></div>
            <div className="flex flex-wrap gap-2 sm:col-span-3">
              <Button onClick={() => saveSelected(edit)} disabled={busy === "save-source"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="source-save-metadata">Save metadata</Button>
              <a href={`${api.defaults.baseURL}/knowledge/sources/${source.id}/file`} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-300 px-3 text-sm font-medium text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="source-open-file"><FileText size={15} /> Open original</a>
              {source.review_status !== "approved" && <Button onClick={() => reviewSource("approved")} variant="outline" className="border-[#27AE60]/40 text-[#1e8449]" data-testid="source-approve"><CheckCircle size={15} className="mr-1.5" /> Approve source</Button>}
              {source.review_status !== "archived" && <Button onClick={() => reviewSource("archived")} variant="outline" className="border-stone-300 text-stone-500" data-testid="source-archive">Archive</Button>}
            </div>
          </div>

          <div className="rounded-lg border border-stone-200 p-4" data-testid="source-ai-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><h3 className="font-heading text-lg font-semibold text-stone-900">AI classification & mapping suggestions</h3><p className="text-xs text-stone-500">One click classifies the document and pulls out its individual requirements for your review. AI suggests only — nothing becomes operational until you approve it.</p></div>
              <div className="flex flex-wrap gap-2">
                {source.extraction_status !== "extracted" && (
                  <Button onClick={reextractSource} disabled={busy === "ocr"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="source-run-ocr">
                    <ArrowClockwise size={15} className="mr-1.5" /> {busy === "ocr" ? "Running OCR..." : "Run OCR"}
                  </Button>
                )}
                <Button onClick={runFullAnalysis} disabled={busy === "ai" || !!extract || source.extraction_status !== "extracted"} variant="outline" className="border-stone-300" data-testid="source-ai-classify">
                  <Robot size={15} className={`mr-1.5 ${busy === "ai" || extract ? "animate-pulse" : ""}`} />
                  {busy === "ai" ? "Classifying…" : extract ? `Extracting requirements… ${extract.processed}/${extract.total ?? "?"} sections` : "Run AI analysis"}
                </Button>
              </div>
            </div>
            {ai ? (
              <div className="mt-4 rounded-lg bg-accent/40 p-4 text-sm" data-testid="ai-classification-result">
                <p className="font-medium text-stone-900">{ai.summary || "No summary returned."}</p>
                <p className="mt-2 text-xs text-stone-500">Suggested: {labelFor(SOURCE_TYPES, ai.source_type)} · {labelFor(AUTHORITY_LEVELS, ai.authority_level)} · tags {(ai.tags || []).join(", ") || "—"}</p>
                <p className="mt-2 text-xs text-stone-600">Policies: {(ai.policy_matches || []).join(", ") || "—"}</p>
                <p className="mt-1 text-xs text-stone-600">Training: {(ai.training_matches || []).join(", ") || "—"}</p>
                {(ai.mapping_evidence || []).length > 0 && (
                  <div className="mt-2 space-y-1 border-t border-stone-200 pt-2" data-testid="ai-mapping-evidence">
                    <p className="text-xs font-semibold text-stone-500">Why these mappings (from the source text):</p>
                    {ai.mapping_evidence.map((e, i) => (
                      <p key={i} className="text-xs text-stone-500" data-testid={`ai-evidence-${i}`}>
                        <span className="font-medium text-stone-700">{e.target}</span> — &ldquo;{e.evidence}&rdquo;
                      </p>
                    ))}
                  </div>
                )}
                <Button onClick={applyAi} disabled={busy === "apply-ai"} size="sm" className="mt-3 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="apply-ai-classification">Apply metadata suggestions</Button>
              </div>
            ) : <p className="mt-4 rounded-lg border border-dashed border-stone-300 p-4 text-sm text-stone-400">No AI suggestion yet.</p>}
          </div>

          <div className="rounded-lg border border-stone-200 p-4" data-testid="source-mapping-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-heading text-lg font-semibold text-stone-900">Approved mappings</h3>
              {source.review_status !== "archived" && (
                <Button onClick={runExtraction} disabled={!!extract || busy === "ai"} variant="outline" className="border-primary/40 text-accent-foreground" data-testid="source-extract-requirements">
                  {extract ? (
                    <><ArrowClockwise size={15} className="mr-1.5 animate-spin" /> Extracting… {extract.processed}/{extract.total ?? "?"} sections</>
                  ) : (
                    <><ListMagnifyingGlass size={15} className="mr-1.5" /> Extract requirements (AI)</>
                  )}
                </Button>
              )}
            </div>
            {extract && (
              <div className="mt-3" data-testid="extract-progress">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: extract.total ? `${Math.max(5, Math.round((extract.processed / extract.total) * 100))}%` : "10%" }} />
                </div>
                <p className="mt-1 text-xs text-stone-500">AI is reading the document — section {extract.processed} of {extract.total ?? "…"}, {extract.created} requirement(s) found so far. It finishes on its own; results land in the Requirements tab as pending review.</p>
              </div>
            )}
            <p className="mt-1 text-xs text-stone-500">Links the AI suggested are pre-checked and marked <span className="rounded bg-accent px-1 font-semibold">AI</span> — uncheck anything wrong, check anything it missed, then Save. Any checked item can carry an optional nuance note (e.g. cadence or how you implement it). Mappings start counting toward the Coverage Matrix and Gap Report once the source itself is approved.</p>
            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <MappingPicker title="Policies" items={policies} selected={mappings.policy_ids} suggested={aiSuggested.policy_ids} notes={mapNotes} onNote={(id, v) => setMapNotes((n) => ({ ...n, [id]: v }))} onToggle={(id) => toggleMap("policy_ids", id)} testid="policy" />
              <MappingPicker title="Training topics" items={topics} selected={mappings.training_topic_ids} suggested={aiSuggested.training_topic_ids} notes={mapNotes} onNote={(id, v) => setMapNotes((n) => ({ ...n, [id]: v }))} onToggle={(id) => toggleMap("training_topic_ids", id)} testid="training" />
              <MappingPicker title="Orientation steps" items={orientationSteps} selected={mappings.orientation_step_ids} suggested={aiSuggested.orientation_step_ids} notes={mapNotes} onNote={(id, v) => setMapNotes((n) => ({ ...n, [id]: v }))} onToggle={(id) => toggleMap("orientation_step_ids", id)} testid="orientation" />
            </div>
            <Button onClick={() => saveMappings(mappings, mapNotes)} disabled={busy === "mappings"} className="mt-4 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="save-source-mappings">Save approved mappings</Button>
          </div>

          <div className="rounded-lg border border-stone-200 p-4" data-testid="source-chunks-panel">
            <h3 className="font-heading text-lg font-semibold text-stone-900">Knowledge chunks ({chunks.length})</h3>
            {source.extraction_status !== "extracted" && <p className="mt-3 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/10 p-3 text-sm text-[#7c4a03]">No extractable text was found. The immutable original is registered, but OCR is needed before this source can power policy/training suggestions.</p>}
            <div className="mt-4 space-y-3">
              {chunks.map((c) => (
                <div key={c.id} className="rounded-lg border border-stone-200 p-4" data-testid={`knowledge-chunk-${c.id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Chunk {c.chunk_index} · page {c.page_start || "—"} · {c.section_title || "Section not identified"}</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-stone-700">{c.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-stone-200 p-4" data-testid="source-versions-panel">
            <h3 className="font-heading text-lg font-semibold text-stone-900">Version history</h3>
            <div className="mt-3 space-y-2">
              {versions.map((v) => <p key={v.id} className="text-sm text-stone-600">v{v.version} · {fmtDate(v.created_at)} · {v.filename} · {v.review_status}</p>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MappingPicker({ title, items, selected, suggested = [], notes, onNote, onToggle, testid }) {
  const ordered = [...items].sort((a, b) => (suggested.includes(b.id) ? 1 : 0) - (suggested.includes(a.id) ? 1 : 0));
  return (
    <div className="rounded-lg border border-stone-200">
      <p className="border-b border-stone-200 bg-stone-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-stone-500">{title}</p>
      <div className="max-h-56 space-y-1 overflow-y-auto p-2">
        {ordered.map((item) => (
          <div key={item.id}>
            <label className="flex items-start gap-2 rounded p-1.5 text-xs text-stone-700 hover:bg-accent/40" data-testid={`mapping-${testid}-${item.id}`}>
              <input type="checkbox" checked={selected.includes(item.id)} onChange={() => onToggle(item.id)} className="mt-0.5" />
              <span>{item.title}{suggested.includes(item.id) && <span className="ml-1.5 rounded bg-accent px-1 py-0.5 text-[10px] font-bold text-accent-foreground" data-testid={`mapping-ai-${testid}-${item.id}`}>AI</span>}</span>
            </label>
            {notes && selected.includes(item.id) && (
              <input value={notes[item.id] || ""} onChange={(e) => onNote(item.id, e.target.value)} placeholder="Nuance — how we implement this (optional)"
                className="mb-1 ml-6 h-7 w-[calc(100%-1.75rem)] rounded border border-stone-200 bg-white px-2 text-[11px] text-stone-600" data-testid={`mapping-note-${testid}-${item.id}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
